import {useEffect, useRef, useState} from "react";
import {useHistory} from "react-router-dom";
import ReactMarkdown from "react-markdown";
import {
  Alert,
  Avatar,
  Box,
  Chip,
  Divider,
  Icon,
  IconButton,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {keyframes} from "@mui/system";

import pxToRem from "hooks/usePxToRem";
import {CustomButton} from "ui";
import {PageTitle} from "components";

import {useNIA} from "./NIAContext";

type Props = {
  mode: "drawer" | "page";
};

const bounce = keyframes`
  0%, 80%, 100% {
    opacity: 0.3;
    transform: scale(0.75);
  }
  40% {
    opacity: 1;
    transform: scale(1);
  }
`;

const TypingIndicator = () => (
  <Stack direction="row" spacing={0.5} sx={{px: pxToRem(4)}}>
    {[0, 1, 2].map((dot) => (
      <Box
        key={dot}
        sx={{
          width: pxToRem(6),
          height: pxToRem(6),
          borderRadius: "50%",
          backgroundColor: "text.secondary",
          animation: `${bounce} 1.2s ease-in-out infinite`,
          animationDelay: `${dot * 0.15}s`,
        }}
      />
    ))}
  </Stack>
);

const NIAWorkspace = ({mode}: Props) => {
  const history = useHistory();
  const {
    cancelMessage,
    clearSelectedTheme,
    closeDrawer,
    currentMessages,
    currentSessionId,
    hasProcessContext,
    originContext,
    resetCurrentConversation,
    selectedTheme,
    sendMessage,
    sending,
    setSelectedTheme,
    suggestions,
    themeOptions,
  } = useNIA();

  const [inputValue, setInputValue] = useState("");
  const listRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!listRef.current) return;

    listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [currentMessages, sending]);

  const handleSendMessage = async () => {
    const content = inputValue.trim();

    if (!content || !selectedTheme) return;

    const hasSentMessage = await sendMessage(content);

    if (hasSentMessage) {
      setInputValue("");
    }
  };

  const handleOpenFullPage = () => {
    closeDrawer();
    history.push("/nia");
  };

  const handleKeyDown = async (
    event: React.KeyboardEvent<HTMLDivElement>,
  ) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      await handleSendMessage();
    }

    if (event.key === "Escape" && sending) {
      event.preventDefault();
      cancelMessage();
    }
  };

  const showThemeSelector = !hasProcessContext && !selectedTheme;
  const drawerMode = mode === "drawer";

  const handleNewConversation = () => {
    resetCurrentConversation();

    if (!hasProcessContext) {
      clearSelectedTheme();
    }
  };

  const content = (
    <Paper
      elevation={drawerMode ? 0 : 1}
      sx={{
        display: "flex",
        flexDirection: "column",
        height: drawerMode ? "100%" : `calc(100vh - ${pxToRem(140)})`,
        minHeight: drawerMode ? "100%" : pxToRem(560),
        borderRadius: drawerMode ? 0 : pxToRem(20),
        overflow: "hidden",
        border: (theme) =>
          drawerMode ? "none" : `1px solid ${theme.palette.divider}`,
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{
          px: pxToRem(20),
          py: pxToRem(16),
          borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
        }}
      >
        <Stack spacing={0.5}>
          <Typography variant="subtitle2" color="text.secondary">
            Next Inteligência Artificial
          </Typography>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography variant="h6" color="primary" component="strong">
              NIA
            </Typography>
            {selectedTheme && (
              <Chip
                size="small"
                color="primary"
                variant="outlined"
                label={selectedTheme.toUpperCase()}
              />
            )}
          </Stack>
        </Stack>

        <Stack direction="row" spacing={0.5}>
          {selectedTheme && (
            <Tooltip title="Nova conversa">
              <IconButton onClick={handleNewConversation} size="small">
                <Icon>add_comment</Icon>
              </IconButton>
            </Tooltip>
          )}

          {drawerMode && (
            <Tooltip title="Abrir em tela cheia">
              <IconButton onClick={handleOpenFullPage} size="small">
                <Icon>open_in_full</Icon>
              </IconButton>
            </Tooltip>
          )}

          {drawerMode && (
            <Tooltip title="Fechar">
              <IconButton onClick={closeDrawer} size="small">
                <Icon>close</Icon>
              </IconButton>
            </Tooltip>
          )}
        </Stack>
      </Stack>

      <Box
        ref={listRef}
        sx={{
          flex: 1,
          overflowY: "auto",
          px: pxToRem(20),
          py: pxToRem(20),
          background: (theme) =>
            theme.palette.mode === "dark"
              ? "linear-gradient(180deg, rgba(17,24,39,0.15) 0%, rgba(0,0,0,0) 100%)"
              : "linear-gradient(180deg, rgba(25,118,210,0.05) 0%, rgba(255,255,255,0) 100%)",
        }}
      >
        {showThemeSelector ? (
          <Stack spacing={2}>
            <Typography variant="subtitle1" color="primary" fontWeight={700}>
              Escolha o tema inicial
            </Typography>

            <Stack spacing={1.5}>
              {themeOptions.map((themeOption) => (
                <Paper
                  key={themeOption.id}
                  variant="outlined"
                  onClick={() => setSelectedTheme(themeOption.id)}
                  sx={{
                    p: pxToRem(14),
                    cursor: "pointer",
                    transition: "border-color 0.2s ease",
                    "&:hover": {
                      borderColor: "primary.main",
                    },
                  }}
                >
                  <Stack spacing={0.25}>
                    <Typography variant="body1" fontWeight={700}>
                      {themeOption.label}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {themeOption.description}
                    </Typography>
                  </Stack>
                </Paper>
              ))}
            </Stack>
          </Stack>
        ) : (
          <>
            {hasProcessContext && (
              <Alert severity="info" sx={{mb: pxToRem(16)}}>
                Processo conectado automaticamente:{" "}
                <strong>
                  {originContext.processNumber ?? originContext.processId}
                </strong>
              </Alert>
            )}

            {currentMessages.length === 0 ? (
              <Stack spacing={2}>
                <Stack spacing={0.25}>
                  <Typography variant="subtitle1" color="primary" fontWeight={700}>
                    Como posso ajudar?
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Use uma das sugestões abaixo ou escreva sua própria
                    mensagem.
                  </Typography>
                </Stack>

                {suggestions.length > 0 && (
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    {suggestions.map((suggestion) => (
                      <Chip
                        key={suggestion.id}
                        label={suggestion.title}
                        variant="outlined"
                        size="small"
                        onClick={() => setInputValue(suggestion.prompt)}
                        sx={{
                          fontWeight: 400,
                          color: "text.secondary",
                          borderColor: "divider",
                        }}
                      />
                    ))}
                  </Stack>
                )}
              </Stack>
            ) : (
              <Stack spacing={2}>
                {currentMessages.map((message) => {
                  const isUserMessage = message.role === "user";

                  return (
                    <Stack
                      key={message.id}
                      direction={isUserMessage ? "row-reverse" : "row"}
                      spacing={1.5}
                      alignItems="flex-start"
                    >
                      <Avatar
                        sx={{
                          bgcolor: isUserMessage
                            ? "primary.main"
                            : "primary.light",
                          color: isUserMessage ? "#fff" : "primary.main",
                        }}
                      >
                        {isUserMessage ? (
                          <Icon>person</Icon>
                        ) : (
                          <Icon>auto_awesome</Icon>
                        )}
                      </Avatar>

                      {isUserMessage ? (
                        <Box
                          sx={{
                            p: pxToRem(12),
                            maxWidth: "85%",
                            borderRadius: pxToRem(8),
                            bgcolor: "#7E38DA",
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{
                              whiteSpace: "pre-wrap",
                              fontSize: pxToRem(13),
                              color: "#fff",
                            }}
                          >
                            {message.content}
                          </Typography>
                        </Box>
                      ) : (
                        <Paper
                          variant="outlined"
                          sx={{
                            p: pxToRem(12),
                            maxWidth: "85%",
                            borderRadius: pxToRem(8),
                          }}
                        >
                          <Box
                            sx={{
                              fontSize: pxToRem(13),
                              "& p": {
                                margin: `${pxToRem(8)} 0`,
                              },
                              "& ul, & ol": {
                                paddingLeft: pxToRem(20),
                              },
                              "& a": {
                                color: "primary.main",
                              },
                            }}
                          >
                            <ReactMarkdown>{message.content}</ReactMarkdown>
                          </Box>
                        </Paper>
                      )}
                    </Stack>
                  );
                })}

                {sending && (
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <Avatar
                      sx={{
                        bgcolor: "primary.light",
                        color: "primary.main",
                      }}
                    >
                      <Icon>auto_awesome</Icon>
                    </Avatar>
                    <Box sx={{p: pxToRem(16)}}>
                      <TypingIndicator />
                    </Box>
                  </Stack>
                )}
              </Stack>
            )}
          </>
        )}
      </Box>

      {!showThemeSelector && (
        <>
          <Divider />

          <Stack spacing={1.5} sx={{p: pxToRem(20)}}>
            <TextField
              fullWidth
              multiline
              minRows={3}
              maxRows={8}
              value={inputValue}
              onChange={(event) => setInputValue(event.target.value)}
              onKeyDown={handleKeyDown}
              disabled={sending}
              placeholder="Escreva sua mensagem para a NIA..."
              inputProps={{style: {fontSize: pxToRem(13)}}}
            />

            <Stack
              direction={{xs: "column", sm: "row"}}
              justifyContent="space-between"
              alignItems={{xs: "stretch", sm: "center"}}
              spacing={1}
            >
              <Typography variant="caption" color="text.secondary">
                NIA pode produzir respostas imprecisas. Sessão{" "}
                {currentSessionId ? currentSessionId.slice(0, 8) : "não iniciada"}.
              </Typography>

              <Stack direction="row" spacing={1}>
                {sending && (
                  <CustomButton
                    variant="outlined"
                    onClickFn={cancelMessage}
                    startIcon="close"
                  >
                    Cancelar
                  </CustomButton>
                )}

                <CustomButton
                  onClickFn={handleSendMessage}
                  disabled={!inputValue.trim() || sending}
                  startIcon="send"
                >
                  Enviar
                </CustomButton>
              </Stack>
            </Stack>
          </Stack>
        </>
      )}
    </Paper>
  );

  if (drawerMode) {
    return (
      <Box
        sx={{
          width: {
            xs: "100vw",
            sm: pxToRem(520),
          },
          height: "100vh",
          backgroundColor: "background.default",
        }}
      >
        {content}
      </Box>
    );
  }

  return (
    <Stack spacing={2}>
      <PageTitle title="NIA" subtitle="Inteligência artificial" />
      {content}
    </Stack>
  );
};

export default NIAWorkspace;
